import { useState, useEffect, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { supabase } from '@/integrations/supabase/client';

const industryMults = [1.3, 1.5, 1.2, 1.4, 1.35, 1.25, 1.3, 1.0];
const autoValues = [0.4, 0.65, 0.85];
const autoPcts = ['40%', '65%', '85%'];

function useAnimatedValue(target: number, decimals = 0) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number>();
  useEffect(() => {
    let current = display;
    const step = (target - current) / 20;
    let frame = 0;
    const animate = () => {
      frame++;
      current += step;
      if (frame >= 20) { setDisplay(target); return; }
      setDisplay(Math.round(current * 10 ** decimals) / 10 ** decimals);
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target]);
  return display;
}

const ROICalculatorSection = () => {
  const { t, language } = useTranslation();
  const roi = t.roi as any;

  const [industry, setIndustry] = useState(0);
  const [autoTarget, setAutoTarget] = useState(0);
  const [team, setTeam] = useState(50);
  const [tasks, setTasks] = useState(35);
  const [salary, setSalary] = useState(45000);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const mult = industryMults[industry];
  const autoVal = autoValues[autoTarget];

  const workHoursPerYear = 2000;
  const taskHours = workHoursPerYear * (tasks / 100);
  const hoursAutomated = taskHours * autoVal * mult;
  const totalHoursSaved = Math.round(hoursAutomated * team);
  const hourlyRate = salary / workHoursPerYear;
  const costSaved = Math.round(totalHoursSaved * hourlyRate);
  const implCost = team < 20 ? 15000 : team < 100 ? 45000 : team < 500 ? 120000 : 300000;
  const roiVal = costSaved / implCost;
  const paybackMonths = Math.ceil((implCost / costSaved) * 12);

  const animHours = useAnimatedValue(totalHoursSaved);
  const animCost = useAnimatedValue(costSaved);
  const animRoi = useAnimatedValue(Math.round(roiVal * 10) / 10, 1);

  const pct = (val: number, min: number, max: number) => ((val - min) / (max - min) * 100).toFixed(1) + '%';

  const handleSubmitReport = async () => {
    if (!name.trim() || !email.trim()) return;
    setSending(true);
    try {
      await supabase.functions.invoke('submit-report-request', {
        body: {
          name: name.trim(),
          email: email.trim(),
          language,
          source: 'roi_calculator',
          input_data: {
            industry: roi.industries[industry],
            industryIndex: industry,
            teamSize: team,
            repetitiveTasks: tasks,
            avgSalary: salary,
            automationTarget: roi.autoTargets[autoTarget],
            automationIndex: autoTarget,
            projectedHoursSaved: totalHoursSaved,
            projectedCostSaved: costSaved,
            projectedROI: Math.round(roiVal * 10) / 10,
            paybackMonths,
          },
        },
      });
      setSent(true);
    } catch (err) {
      console.error('Report request error:', err);
    }
    setSending(false);
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setSent(false);
    setIndustry(0);
    setAutoTarget(0);
    setTeam(50);
    setTasks(35);
    setSalary(45000);
  };

  return (
    <section id="roi-calculator" className="roi-section">
      <div className="roi-header-wrap">
        <div className="section-tag" style={{ color: 'var(--amber)', justifyContent: 'center' }}>
          <span>{roi.tag}</span>
        </div>
        <h2 className="section-h" style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto 18px' }}>
          {roi.title} <span className="a">{roi.titleAccent}</span>
        </h2>
        <p className="section-sub" style={{ margin: '0 auto', textAlign: 'center' }}>
          {roi.subtitle}
        </p>
      </div>

      <div className="roi-wrapper">
        <div className="roi-controls">
          <h3>{roi.profileTitle}</h3>

          <div className="roi-field">
            <label>{roi.industryLabel}</label>
            <div className="roi-pills">
              {roi.industries.map((ind: string, i: number) => (
                <button key={ind} className={`roi-pill${i === industry ? ' sel' : ''}`} onClick={() => setIndustry(i)}>{ind}</button>
              ))}
            </div>
          </div>

          <div className="roi-field">
            <label>{roi.teamLabel} <span>{team} {roi.teamUnit}</span></label>
            <div className="roi-slider-wrap">
              <input type="range" className="roi-slider" min={5} max={500} value={team}
                style={{ '--pct': pct(team, 5, 500) } as React.CSSProperties}
                onChange={e => setTeam(+e.target.value)} />
            </div>
          </div>

          <div className="roi-field">
            <label>{roi.tasksLabel} <span>{tasks}% {roi.tasksUnit}</span></label>
            <div className="roi-slider-wrap">
              <input type="range" className="roi-slider" min={5} max={80} value={tasks}
                style={{ '--pct': pct(tasks, 5, 80) } as React.CSSProperties}
                onChange={e => setTasks(+e.target.value)} />
            </div>
          </div>

          <div className="roi-field">
            <label>{roi.salaryLabel} <span>€{salary.toLocaleString()} {roi.salaryUnit}</span></label>
            <div className="roi-slider-wrap">
              <input type="range" className="roi-slider" min={20000} max={120000} step={1000} value={salary}
                style={{ '--pct': pct(salary, 20000, 120000) } as React.CSSProperties}
                onChange={e => setSalary(+e.target.value)} />
            </div>
          </div>

          <div className="roi-field">
            <label>{roi.automationLabel}</label>
            <div className="roi-pills">
              {roi.autoTargets.map((at: string, i: number) => (
                <button key={at} className={`roi-pill${i === autoTarget ? ' sel' : ''}`} onClick={() => setAutoTarget(i)}>
                  {at} {autoPcts[i]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="roi-results">
          <h3>{roi.resultsTitle}</h3>

          <div className="roi-metric">
            <div className="roi-metric-label">{roi.hoursSaved}</div>
            <div className="roi-metric-value">{animHours.toLocaleString()} {roi.hrs}</div>
            <div className="roi-metric-sub">{roi.hoursSavedSub}</div>
            <div className="roi-bar"><div className="roi-bar-fill" style={{ width: Math.min(totalHoursSaved / 50000 * 100, 100) + '%' }} /></div>
          </div>

          <div className="roi-metric">
            <div className="roi-metric-label">{roi.costSavings}</div>
            <div className="roi-metric-value">€{animCost.toLocaleString()}</div>
            <div className="roi-metric-sub">{roi.costSavingsSub}</div>
            <div className="roi-bar"><div className="roi-bar-fill" style={{ width: Math.min(costSaved / 2000000 * 100, 100) + '%' }} /></div>
          </div>

          <div className="roi-metric">
            <div className="roi-metric-label">{roi.roiMultiplier}</div>
            <div className="roi-metric-value">{animRoi.toFixed(1)}×</div>
            <div className="roi-metric-sub">{roi.roiMultiplierSub}</div>
            <div className="roi-bar"><div className="roi-bar-fill" style={{ width: Math.min(roiVal / 20 * 100, 100) + '%' }} /></div>
          </div>

          <div className="roi-metric">
            <div className="roi-metric-label">{roi.paybackPeriod}</div>
            <div className="roi-metric-value">
              {paybackMonths <= 12 ? paybackMonths + ' ' + roi.months : (paybackMonths / 12).toFixed(1) + ' ' + roi.years}
            </div>
            <div className="roi-metric-sub">{roi.paybackSub}</div>
          </div>

          <div className="roi-cta-box">
            {sent ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>✅</div>
                <h4 style={{ fontFamily: 'var(--f-display)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--blue)', marginBottom: 8 }}>{roi.confirmTitle}</h4>
                <p style={{ fontSize: '.88rem', color: 'var(--slate)', lineHeight: 1.6 }}>{roi.confirmMessage}</p>
                <button className="roi-cta-btn" onClick={resetForm} style={{ marginTop: 16, background: 'transparent', color: 'var(--blue)', border: '1.5px solid var(--blue)' }}>
                  {roi.confirmAnother}
                </button>
              </div>
            ) : (
              <>
                <p>{roi.ctaText}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--slate)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                      {roi.nameLabel}
                    </label>
                    <input
                      type="text" value={name} onChange={e => setName(e.target.value)}
                      placeholder={roi.namePlaceholder}
                      style={{
                        width: '100%', padding: '10px 14px', border: '1.5px solid var(--line-slate)',
                        borderRadius: 10, fontSize: '.86rem', fontFamily: 'var(--f-body)',
                        outline: 'none', background: 'var(--white)', color: 'var(--ink)',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, color: 'var(--slate)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 6 }}>
                      {roi.emailLabel}
                    </label>
                    <input
                      type="email" value={email} onChange={e => setEmail(e.target.value)}
                      placeholder={roi.emailPlaceholder}
                      style={{
                        width: '100%', padding: '10px 14px', border: '1.5px solid var(--line-slate)',
                        borderRadius: 10, fontSize: '.86rem', fontFamily: 'var(--f-body)',
                        outline: 'none', background: 'var(--white)', color: 'var(--ink)',
                      }}
                    />
                  </div>
                </div>
                <button
                  className="roi-cta-btn"
                  onClick={handleSubmitReport}
                  disabled={!name.trim() || !email.trim() || sending}
                  style={{ marginTop: 16, opacity: (!name.trim() || !email.trim() || sending) ? 0.6 : 1 }}
                >
                  {sending ? roi.sending : roi.ctaButton}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculatorSection;
